import { listingCategoryHref } from '@/constants/module-url-prefix';
import { type ListingStockProfile } from '@/types';
import { Link } from '@inertiajs/react';

interface ListingStockAboutProps {
    profile: ListingStockProfile;
}

/**
 * Bloco "Sobre" no fim do estoque. Usa só dados curtos do cadastro (não o `description`,
 * para não duplicar o perfil) e esconde qualquer linha sem dado.
 */
export function ListingStockAbout({ profile }: ListingStockAboutProps) {
    const cityState = [profile.city, profile.state_code].filter(Boolean).join(' - ');
    const address = [profile.address, cityState, profile.zipcode].filter(Boolean).join(', ');
    const businessHours = profile.business_hours.filter((hour) => hour.day_name || hour.day_of_week);
    const hasDetails = Boolean(address) || businessHours.length > 0;

    return (
        <section className="mt-8 space-y-4 rounded-xl bg-white p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">Sobre {profile.title}</h2>

            {profile.categories.length > 0 && (
                <p className="text-sm text-gray-600">
                    {profile.categories.map((category, index) => {
                        const href = listingCategoryHref(category.friendly_url_path);

                        return (
                            <span key={category.id}>
                                {index > 0 && <span aria-hidden="true"> · </span>}
                                {href ? (
                                    <a href={href} className="font-medium text-primary hover:underline">
                                        {category.name}
                                    </a>
                                ) : (
                                    <span className="font-medium text-primary">{category.name}</span>
                                )}
                            </span>
                        );
                    })}
                </p>
            )}

            {profile.summary && <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700 sm:text-base">{profile.summary}</p>}

            {profile.services.length > 0 && (
                <p className="text-sm text-gray-700 sm:text-base">
                    <span className="font-semibold text-gray-900">Serviços:</span> {profile.services.join(', ')}
                </p>
            )}

            {hasDetails && (
                <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 sm:text-base">
                    {address && (
                        <div>
                            <dt className="font-semibold text-gray-900">Endereço</dt>
                            <dd className="mt-1 text-gray-700">{address}</dd>
                        </div>
                    )}

                    {businessHours.length > 0 && (
                        <div>
                            <dt className="font-semibold text-gray-900">Horário de funcionamento</dt>
                            <dd className="mt-1">
                                <ul className="space-y-0.5 text-gray-700">
                                    {businessHours.map((hour) => (
                                        <li key={hour.day_of_week} className="flex justify-between gap-3 sm:max-w-xs">
                                            <span>{hour.day_name || hour.day_of_week}</span>
                                            <span className="text-right text-gray-600">
                                                {hour.formatted_hours || (hour.is_closed ? 'Fechado' : `${hour.opening_time} - ${hour.closing_time}`)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </dd>
                        </div>
                    )}
                </dl>
            )}

            <Link href={profile.profile_path} className="inline-block text-sm font-semibold text-primary hover:underline sm:text-base">
                Ver perfil completo →
            </Link>
        </section>
    );
}
